import { api, unwrap } from '../api/client';
import {
  REALTIME_PROTOCOL_VERSION,
  type TRealtimeFrameOf,
  type TRealtimeFrameType,
  type TRealtimeServerFrame,
} from './protocol';

export type TRealtimeStatus = 'idle' | 'connecting' | 'live' | 'offline' | 'unavailable';

type TAnyListener = (frame: TRealtimeServerFrame) => void;

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';
const CONNECT_PATH = '/v1/realtime/connect';

const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 15_000];

const HELLO_GRACE_MS = 10_000;

const DEFAULT_SILENCE_MS = 50_000;

const connectUrl = (ticket: string): string => {
  const url = new URL(`${BASE}${CONNECT_PATH}`, window.location.origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('ticket', ticket);
  return url.toString();
};

export class RealtimeConnection {
  private socket: WebSocket | null = null;
  private state: TRealtimeStatus = 'idle';
  private wanted = false;
  private attempt = 0;
  private retryTimer: number | null = null;
  private watchdogTimer: number | null = null;
  private listening = false;
  private silenceMs = DEFAULT_SILENCE_MS;

  // Bumped on every (re)connect so a ticket minted for an abandoned attempt cannot open a second socket behind the one that replaced it.
  private generation = 0;

  private ready: TRealtimeFrameOf<'ready'> | null = null;
  private resumeFrom: string | null = null;

  private readonly listeners = new Map<TRealtimeFrameType, Set<TAnyListener>>();
  private readonly statusListeners = new Set<() => void>();

  status(): TRealtimeStatus {
    return this.state;
  }

  lastReady(): TRealtimeFrameOf<'ready'> | null {
    return this.ready;
  }

  subscribeStatus(listener: () => void): () => void {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  on<K extends TRealtimeFrameType>(
    type: K,
    listener: (frame: TRealtimeFrameOf<K>) => void,
  ): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    const stored = listener as unknown as TAnyListener;
    set.add(stored);
    return () => {
      set.delete(stored);
    };
  }

  connect(): void {
    if (this.wanted) return;
    this.wanted = true;
    this.attempt = 0;
    this.listenToBrowser();
    this.open();
  }

  disconnect(): void {
    this.wanted = false;
    this.generation += 1;
    this.clearRetry();
    this.clearWatchdog();
    this.stopListeningToBrowser();
    const socket = this.socket;
    this.socket = null;
    socket?.close(1000, 'client disconnect');
    this.setStatus('idle');
  }

  reconnect(): void {
    this.wanted = true;
    this.attempt = 0;
    this.clearRetry();
    this.clearWatchdog();
    const socket = this.socket;
    this.generation += 1;
    this.socket = null;
    socket?.close(4000, 'client reconnect');
    this.open();
  }

  // A ticket lives twenty seconds: mint it immediately before its socket, never ahead of time and never twice.
  private open(): void {
    if (!this.wanted || this.socket) return;
    const generation = ++this.generation;
    this.setStatus('connecting');

    void unwrap(api.POST('/v1/realtime/ticket', {}))
      .then((minted) => {
        if (generation !== this.generation || !this.wanted) return;
        this.attach(new WebSocket(connectUrl(minted.ticket)), generation);
      })
      .catch(() => this.scheduleRetry(generation));
  }

  private attach(socket: WebSocket, generation: number): void {
    this.socket = socket;

    socket.onopen = () => {
      const hello = {
        type: 'hello' as const,
        protocolVersion: REALTIME_PROTOCOL_VERSION,
        ...(this.resumeFrom ? { resumeFrom: this.resumeFrom } : {}),
      };
      socket.send(JSON.stringify(hello));
      this.armWatchdog(HELLO_GRACE_MS);
    };

    socket.onmessage = (message) => this.receive(socket, String(message.data));

    socket.onclose = () => {
      if (generation !== this.generation) return;
      this.socket = null;
      this.clearWatchdog();
      this.scheduleRetry(generation);
    };
  }

  private receive(socket: WebSocket, raw: string): void {
    let frame: TRealtimeServerFrame;
    try {
      frame = JSON.parse(raw) as TRealtimeServerFrame;
    } catch {
      return;
    }

    this.armWatchdog(this.silenceMs);

    switch (frame.type) {
      case 'ready':
        this.ready = frame;
        this.attempt = 0;
        this.silenceMs = Math.max(frame.heartbeatSec * 2_500, 10_000);
        this.setStatus('live');
        break;

      case 'event.created':
        if (!this.resumeFrom || frame.event.at > this.resumeFrom) {
          this.resumeFrom = frame.event.at;
        }
        break;

      case 'resync':
        // The cursor is thrown away on purpose: resuming from it trips the same window again, and the screen's refetch covers the gap.
        this.resumeFrom = new Date().toISOString();
        break;

      case 'ping':
        socket.send(JSON.stringify({ type: 'pong', ts: frame.ts }));
        break;

      case 'error':
        if (frame.fatal) {
          this.wanted = false;
          this.setStatus('unavailable');
        }
        break;

      default:
        break;
    }

    for (const listener of this.listeners.get(frame.type) ?? []) listener(frame);
  }

  private scheduleRetry(generation: number): void {
    if (generation !== this.generation) return;
    this.socket = null;

    if (!this.wanted) {
      if (this.state !== 'unavailable') this.setStatus('idle');
      return;
    }

    // No attempt ceiling. Giving up left the screen frozen with no way back
    // but a reload, and the reason is usually a laptop lid or a wifi switch.
    this.setStatus('offline');
    const delay = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)] ?? 15_000;
    this.attempt += 1;
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      this.open();
    }, delay);
  }

  // The backoff is a floor, not a schedule: coming back online or back to the
  // tab is better evidence than a timer. A socket that survived a sleeping
  // laptop is usually already dead, so it gets a short window to prove it is
  // not rather than being trusted.
  private readonly wake = (): void => {
    if (!this.wanted || document.visibilityState === 'hidden') return;

    if (this.socket) {
      this.armWatchdog(HELLO_GRACE_MS);
      return;
    }

    this.attempt = 0;
    this.clearRetry();
    this.open();
  };

  private listenToBrowser(): void {
    if (this.listening) return;
    this.listening = true;
    window.addEventListener('online', this.wake);
    document.addEventListener('visibilitychange', this.wake);
  }

  private stopListeningToBrowser(): void {
    if (!this.listening) return;
    this.listening = false;
    window.removeEventListener('online', this.wake);
    document.removeEventListener('visibilitychange', this.wake);
  }

  private armWatchdog(ms: number): void {
    this.clearWatchdog();
    this.watchdogTimer = window.setTimeout(() => {
      this.watchdogTimer = null;
      this.socket?.close(4000, 'no frames received');
    }, ms);
  }

  private clearWatchdog(): void {
    if (this.watchdogTimer !== null) window.clearTimeout(this.watchdogTimer);
    this.watchdogTimer = null;
  }

  private clearRetry(): void {
    if (this.retryTimer !== null) window.clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private setStatus(next: TRealtimeStatus): void {
    if (this.state === next) return;
    this.state = next;
    for (const listener of this.statusListeners) listener();
  }
}
